import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_TINYCMS_FEED_KEYS,
  GET_TINYCMS_SITE,
  CREATE_TINYCMS_FEED_KEY,
  REVOKE_TINYCMS_FEED_KEY,
} from '../../graphql';
import { useCopy } from '../../lib/copyToClipboard';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

/**
 * Site settings → Licensed feeds. The site's free feed (/feed.xml) has titles
 * and summaries; a key lets a licensee (a publisher, an app, a partner) pull
 * the published articles in full from /feeds/licensed.xml or .json, or page
 * through them with the REST API at /feeds/v1/articles. The owner issues one
 * key per licensee, sees what each takes, and can turn any off.
 *
 * A key is shown once, when it is made (the server keeps only a hash). Keys
 * are managed here by account admins and never over MCP; members see the list
 * read-only (the server enforces it).
 *
 * Pro only. On another plan the free feed stays, new keys are offered as an
 * upgrade, and existing keys are kept but paused until the site is back on Pro.
 */

interface FeedKey {
  id: string;
  label: string;
  keyPrefix: string;
  collections: string[];
  tags: string[];
  expiresAt: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  status: 'active' | 'expired' | 'revoked';
  usage30d?: { requests: number; items: number };
}

interface Collection {
  slug: string;
  name: string;
}

const STATUS: Record<FeedKey['status'], { label: string; bg: string; color: string }> = {
  active: { label: 'Active', bg: 'var(--ink-green-bg)', color: 'var(--ink-green-ink)' },
  expired: { label: 'Expired', bg: 'var(--ink-amber-bg)', color: 'var(--ink-amber)' },
  revoked: { label: 'Turned off', bg: 'var(--ink-bg3)', color: 'var(--ink-ink3)' },
};

const PLAN_LINK = '/settings?section=billing';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const ago = (iso: string) => {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

const count = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Tomorrow, as a date input's min. */
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

function scopeText(k: FeedKey, names: Map<string, string>) {
  const parts: string[] = [];
  if (k.collections.length) parts.push(k.collections.map((s) => names.get(s) || s).join(', '));
  if (k.tags.length) parts.push(`tagged ${k.tags.join(', ')}`);
  // Both set: an article must match a collection AND a tag (the server's rule).
  return parts.length ? `Only ${parts.join(', ')}` : 'All articles';
}

export function FeedKeysSettings() {
  const { accountId } = useAccount();
  const { copy, state: copyState } = useCopy();
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<FeedKey | null>(null);
  const [showOff, setShowOff] = useState(false);

  const { data, loading, refetch } = useQuery(GET_TINYCMS_FEED_KEYS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: siteData } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const info = data?.tinycmsFeedKeys;
  const keys: FeedKey[] = useMemo(() => info?.keys || [], [info]);
  const canManage = Boolean(info?.canManage);
  // Older servers don't send it: treat as allowed and let the server refuse.
  const allowed = info?.allowed !== false;
  const siteUrl: string | null = siteData?.tinycmsSite?.siteUrl || null;
  const collections: Collection[] = useMemo(
    () => siteData?.tinycmsSite?.working?.collections || [],
    [siteData]
  );
  const names = useMemo(() => new Map(collections.map((c) => [c.slug, c.name])), [collections]);

  const [revoke, { loading: revokingNow }] = useMutation(REVOKE_TINYCMS_FEED_KEY, {
    onError: (err) => setError(err.message),
    onCompleted: () => {
      setRevoking(null);
      refetch();
    },
  });

  if (loading && !info) return <Loader label="Loading" className="p-6 text-cmp-ink-2" />;

  const live = keys.filter((k) => k.status === 'active');
  const off = keys.filter((k) => k.status !== 'active');
  const shown = showOff ? keys : live;

  const copyLabel = (key: string, idle = 'Copy') =>
    copyState(key) === 'copied' ? 'Copied' : copyState(key) === 'failed' ? 'Couldn’t copy' : idle;

  const addresses: Array<{ key: string; name: string; desc: string; path: string; paid: boolean }> =
    [
      {
        key: 'xml',
        name: 'Full articles · RSS',
        desc: 'For news readers, publishers and most website tools',
        path: '/feeds/licensed.xml',
        paid: true,
      },
      {
        key: 'json',
        name: 'Full articles · JSON',
        desc: 'The same feed in JSON Feed format, for feed tools that prefer it',
        path: '/feeds/licensed.json',
        paid: true,
      },
      {
        key: 'api',
        name: 'Articles API',
        desc: 'For developers: articles as JSON, a page at a time. Add /<id or address> for one article.',
        path: '/feeds/v1/articles',
        paid: true,
      },
      {
        key: 'free',
        name: 'Free feed',
        desc: 'Titles and summaries, for anyone. No key needed.',
        path: '/feed.xml',
        paid: false,
      },
    ].filter((a) => allowed || !a.paid);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="ink-site-card">
        <div className="ink-site-row">
          <span>
            <span className="ink-site-row-title">Keys</span>
            <span className="ink-site-row-desc" style={{ display: 'block' }}>
              {!allowed
                ? 'Licensing your content comes with the Pro plan. Your free feed works on every plan.'
                : live.length
                  ? `${count(live.length, 'active key')}. Each one shows what it took in the last 30 days.`
                  : off.length
                    ? 'No keys are working right now. Make one for each person or company you license to.'
                    : 'No one has a key yet. Make one for each person or company you license to.'}
            </span>
          </span>
          {!allowed ? (
            canManage ? (
              <Link to={PLAN_LINK} className="ink-btn">
                Change plan
              </Link>
            ) : null
          ) : canManage ? (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => {
                setError('');
                setCreating(true);
              }}
            >
              New key
            </button>
          ) : null}
        </div>
        {!allowed && live.length ? (
          <p className="ink-bill-note" data-tone="warn" style={{ margin: '14px 0' }}>
            Your keys are kept, but they don’t work on this plan: anyone using one is turned away.
            They start working again when you move back to Pro.
          </p>
        ) : null}
        {shown.map((k) => {
          const s =
            !allowed && k.status === 'active'
              ? { label: 'Paused', bg: 'var(--ink-amber-bg)', color: 'var(--ink-amber)' }
              : STATUS[k.status];
          const usage = k.usage30d || { requests: 0, items: 0 };
          return (
            <div
              key={k.id}
              className="ink-site-row"
              style={k.status === 'active' ? undefined : { opacity: 0.7 }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="ink-site-row-title">{k.label}</span>
                  <span className="ink-site-pill" style={{ background: s.bg, color: s.color }}>
                    {s.label}
                  </span>
                </div>
                <div className="ink-site-row-desc">
                  <span style={{ fontFamily: 'var(--ink-mono)' }}>{k.keyPrefix}…</span> ·{' '}
                  {scopeText(k, names)}
                </div>
                <div className="ink-site-row-desc">
                  Made {day(k.createdAt)}
                  {k.revokedAt
                    ? ` · Turned off ${day(k.revokedAt)}`
                    : k.expiresAt
                      ? ` · ${k.status === 'expired' ? 'Expired' : 'Expires'} ${day(k.expiresAt)}`
                      : ' · Never expires'}
                  {' · '}
                  {k.lastUsedAt ? `Last used ${ago(k.lastUsedAt)}` : 'Not used yet'}
                </div>
              </div>
              <div style={{ textAlign: 'right', display: 'grid', gap: 6, justifyItems: 'end' }}>
                <span style={{ font: '500 13.5px/1.3 var(--ink-font)', color: 'var(--ink-ink)' }}>
                  {count(usage.items, 'article')}
                </span>
                <span className="ink-site-row-desc" style={{ marginTop: 0 }}>
                  {count(usage.requests, 'request')} · 30 days
                </span>
                {canManage && k.status === 'active' ? (
                  <button
                    type="button"
                    className="ink-site-link"
                    style={{ color: 'var(--ink-red)' }}
                    onClick={() => {
                      setError('');
                      setRevoking(k);
                    }}
                  >
                    Turn off
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
        {off.length ? (
          <div className="ink-site-hint">
            <button type="button" className="ink-site-link" onClick={() => setShowOff(!showOff)}>
              {showOff ? 'Hide' : 'Show'} {count(off.length, 'old key')}
            </button>
          </div>
        ) : null}
        {error && !creating && !revoking ? (
          <p className="ink-site-hint" style={{ color: 'var(--ink-red)' }}>
            {error}
          </p>
        ) : null}
        {!canManage ? (
          <p className="ink-site-hint">Only an account admin can make or turn off keys.</p>
        ) : null}
      </div>

      <div className="ink-site-card">
        <div className="ink-site-row">
          <span>
            <span className="ink-site-row-title">Feed & API addresses</span>
            <span className="ink-site-row-desc" style={{ display: 'block' }}>
              {allowed
                ? 'Send these to each licensee, with their key.'
                : 'Anyone can follow your site with this address.'}
            </span>
          </span>
          <span />
        </div>
        {siteUrl ? (
          <>
            {addresses.map((a) => (
              <div key={a.key} className="ink-site-row">
                <div style={{ minWidth: 0 }}>
                  <div className="ink-site-row-title">{a.name}</div>
                  <div
                    className="ink-site-row-desc"
                    style={{
                      fontFamily: 'var(--ink-mono)',
                      overflowWrap: 'anywhere',
                      color: 'var(--ink-ink2)',
                    }}
                  >
                    {siteUrl}
                    {a.path}
                  </div>
                  <div className="ink-site-row-desc">{a.desc}</div>
                </div>
                <button
                  type="button"
                  className="ink-site-link"
                  onClick={(e) => copy(a.key, `${siteUrl}${a.path}`, e)}
                >
                  {copyLabel(a.key)}
                </button>
              </div>
            ))}
            {allowed ? (
              <p className="ink-site-hint">
                The key goes in a request header, so it never shows up in an address:{' '}
                <span style={{ fontFamily: 'var(--ink-mono)', color: 'var(--ink-ink2)' }}>
                  Authorization: Bearer tcf_…
                </span>{' '}
                Up to 60 requests a minute per key.
              </p>
            ) : null}
          </>
        ) : (
          <p className="ink-site-hint">
            Your site needs a web address first. Set one under Domain above.
          </p>
        )}
      </div>

      {creating ? (
        <NewKeyModal
          accountId={accountId}
          collections={collections}
          siteUrl={siteUrl}
          onClose={() => {
            setCreating(false);
            refetch();
          }}
        />
      ) : null}

      {revoking ? (
        <div
          className="ink-modal-back"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !revokingNow) setRevoking(null);
          }}
        >
          <div
            className="ink-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="revoke-key-title"
          >
            <div className="ink-modal-title" id="revoke-key-title">
              Turn off “{revoking.label}”?
            </div>
            <div className="ink-modal-copy">
              The key <span style={{ fontFamily: 'var(--ink-mono)' }}>{revoking.keyPrefix}…</span>{' '}
              stops working right away, and this can’t be undone. To let them back in, make a new
              key.
            </div>
            {error ? (
              <div className="ink-modal-copy" style={{ color: 'var(--ink-red)' }}>
                {error}
              </div>
            ) : null}
            <div className="ink-modal-actions">
              <button type="button" className="ink-btn" autoFocus onClick={() => setRevoking(null)}>
                Keep it
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                style={{ background: 'var(--ink-red)', borderColor: 'var(--ink-red)' }}
                disabled={revokingNow}
                onClick={() => revoke({ variables: { accountId, id: revoking.id } })}
              >
                {revokingNow ? 'Turning off…' : 'Turn off key'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Make a key: who it's for, what it can read, when it stops. Then show it once. */
function NewKeyModal({
  accountId,
  collections,
  siteUrl,
  onClose,
}: {
  accountId?: string | null;
  collections: Collection[];
  siteUrl: string | null;
  onClose: () => void;
}) {
  const { copy, state: copyState } = useCopy();
  const [label, setLabel] = useState('');
  const [limit, setLimit] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [tags, setTags] = useState('');
  const [expires, setExpires] = useState('');
  const [error, setError] = useState('');
  const [made, setMade] = useState<(FeedKey & { key: string }) | null>(null);

  const [create, { loading }] = useMutation(CREATE_TINYCMS_FEED_KEY, {
    onError: (err) => setError(err.message),
    onCompleted: (res) => setMade(res.createTinycmsFeedKey),
  });

  const tagList = tags
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const scopeEmpty = limit && !picked.length && !tagList.length;
  const scopeHint =
    picked.length && tagList.length
      ? 'They get articles that are in a collection you picked and also have a tag you picked.'
      : tagList.length || !collections.length
        ? 'They get articles with any of these tags.'
        : 'They get articles in any collection you pick.';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!label.trim()) return setError('Say who the key is for.');
    if (scopeEmpty) return setError('Pick at least one collection or tag, or choose All articles.');
    create({
      variables: {
        accountId,
        input: {
          label: label.trim(),
          ...(limit && picked.length ? { collections: picked } : {}),
          ...(limit && tagList.length ? { tags: tagList } : {}),
          // The end of the chosen day, where the owner is.
          ...(expires ? { expiresAt: new Date(`${expires}T23:59:59`).toISOString() } : {}),
        },
      },
    });
  };

  const copyLabel = (key: string, idle: string) =>
    copyState(key) === 'copied'
      ? 'Copied'
      : copyState(key) === 'failed'
        ? 'Couldn’t copy — select it instead'
        : idle;

  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        // Never on a click outside once the key is showing: it can't be shown again.
        if (e.target === e.currentTarget && !made && !loading) onClose();
      }}
    >
      <div
        className="ink-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-key-title"
        style={{ width: 'min(500px, 100%)' }}
      >
        {made ? (
          <>
            <div className="ink-modal-title" id="new-key-title">
              Key for {made.label}
            </div>
            <div className="ink-modal-copy">
              Copy it now and keep it somewhere safe. <strong>We won’t show it again</strong> — if
              it’s lost, turn it off and make a new one.
            </div>
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                borderRadius: 8,
                background: 'var(--ink-bg2)',
                border: '1px solid var(--ink-line3)',
                font: '500 13.5px/1.5 var(--ink-mono)',
                color: 'var(--ink-ink)',
                overflowWrap: 'anywhere',
                userSelect: 'all',
              }}
              data-feed-key
            >
              {made.key}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              <button type="button" className="ink-btn" onClick={(e) => copy('key', made.key, e)}>
                {copyLabel('key', 'Copy key')}
              </button>
              {siteUrl ? (
                <button
                  type="button"
                  className="ink-btn"
                  onClick={(e) =>
                    copy(
                      'note',
                      [
                        `Feed (RSS): ${siteUrl}/feeds/licensed.xml`,
                        `Feed (JSON): ${siteUrl}/feeds/licensed.json`,
                        `API: ${siteUrl}/feeds/v1/articles (one article: ${siteUrl}/feeds/v1/articles/<id or slug>)`,
                        `Send this header with each request: Authorization: Bearer ${made.key}`,
                      ].join('\n'),
                      e
                    )
                  }
                >
                  {copyLabel('note', 'Copy key with addresses')}
                </button>
              ) : null}
            </div>
            <div className="ink-modal-actions">
              <button type="button" className="ink-btn ink-btn-primary" autoFocus onClick={onClose}>
                I’ve saved it
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="ink-modal-title" id="new-key-title">
              New key
            </div>
            <div className="ink-modal-copy">
              One key for each person or company, so you can see what each takes and turn one off
              without affecting the rest.
            </div>
            <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
              <label className="ink-site-label">
                Who it’s for
                <input
                  className="ink-site-input"
                  value={label}
                  autoFocus
                  maxLength={120}
                  placeholder="e.g. Acme News"
                  onChange={(e) => setLabel(e.target.value)}
                />
              </label>
              <div className="ink-site-label">
                What they can read
                <span className="ink-seg" style={{ alignSelf: 'flex-start' }}>
                  <button type="button" data-active={!limit} onClick={() => setLimit(false)}>
                    All articles
                  </button>
                  <button type="button" data-active={limit} onClick={() => setLimit(true)}>
                    Only some
                  </button>
                </span>
                {limit ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {collections.length ? (
                      <div>
                        <div className="ink-site-row-desc" style={{ marginBottom: 6 }}>
                          Collections
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {collections.map((c) => {
                            const on = picked.includes(c.slug);
                            return (
                              <button
                                key={c.slug}
                                type="button"
                                className="ink-chip"
                                data-active={on}
                                aria-pressed={on}
                                onClick={() =>
                                  setPicked(
                                    on ? picked.filter((s) => s !== c.slug) : [...picked, c.slug]
                                  )
                                }
                              >
                                {c.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    <label className="ink-site-label" style={{ fontWeight: 400 }}>
                      <span className="ink-site-row-desc" style={{ margin: 0 }}>
                        Tags, separated by commas
                      </span>
                      <input
                        className="ink-site-input"
                        value={tags}
                        placeholder="e.g. recipes, travel"
                        onChange={(e) => setTags(e.target.value)}
                      />
                    </label>
                    <span className="ink-site-row-desc" style={{ margin: 0 }}>
                      {scopeHint}
                    </span>
                  </div>
                ) : null}
              </div>
              <label className="ink-site-label">
                Stops working on
                <input
                  className="ink-site-input"
                  type="date"
                  value={expires}
                  min={tomorrow()}
                  onChange={(e) => setExpires(e.target.value)}
                />
                <span className="ink-site-row-desc" style={{ margin: 0, fontWeight: 400 }}>
                  Optional. Leave empty and it works until you turn it off.
                </span>
              </label>
            </div>
            {error ? (
              <div className="ink-modal-copy" style={{ color: 'var(--ink-red)' }}>
                {error}
              </div>
            ) : null}
            <div className="ink-modal-actions">
              <button type="button" className="ink-btn" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="ink-btn ink-btn-primary" disabled={loading}>
                {loading ? 'Making key…' : 'Make key'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
